/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Search component for LabelEditor
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.0.0
 */
import { ChangeEvent } from 'react';

import { useDispatch } from 'react-redux';

import { icons } from '../../../../../../assets';
import { appHistoryPush } from '../../../../../../modules/app/appHistory';
import { ILabelEditorTranslation } from '../../../../../../modules/internationalization/types/corporate';
import { wizardStorageClearStepsAction } from '../../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../../modules/wizard/wizardConstants';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import { Input, LinkBlock } from '../../../../../common';
import LabelEditorCreate from './LabelEditorCreate';
import { LabelEditorImportStyled, LabelEditorSearchStyled } from './styled/LabelEditorStyled';

export interface ILabelEditorSearchProps {
    createLabel: (payload: string) => void;
    isLabelsImportEnabled?: boolean;
    isLoading?: boolean;
    searchLabel: (payload: string) => void;
    searchValue: string;
    translation: ILabelEditorTranslation;
}

const LabelEditorSearch = (props: ILabelEditorSearchProps) => {

    const dispatch = useDispatch();

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        props.searchLabel(value);
    };

    const onInputIconClick = () => {
        props.searchLabel('');
    };

    const onLabelsClick = () => {
        dispatch(wizardStorageClearStepsAction({
            wizardName: EWizardName.LABELS_IMPORT,
        }));
        appHistoryPush(RoutePath.labelsImport);
    }

    return (
        <LabelEditorSearchStyled data-component="labelsSearch" isLoading={props.isLoading}>
            <Input
                autoFocus={true}
                dataName="searchInput"
                placeholder={props.translation.searchPlaceholder}
                righticon={props.searchValue.length ? icons.cancelIcon : icons.searchGreenIcon}
                righticonhandler={onInputIconClick}
                value={props.searchValue}
                onChange={onInputChange}
            />
            <LabelEditorCreate
                translation={props.translation}
                onCreate={props.createLabel}
            >
                <LinkBlock dataName="createButton">
                    {props.translation.btnCreate}
                </LinkBlock>
            </LabelEditorCreate>
            {
                props.isLabelsImportEnabled &&
                <LabelEditorImportStyled>
                    <LinkBlock
                        dataName="importButton"
                        url={RoutePath.labelsImport}
                        onClick={onLabelsClick}
                    >
                        {props.translation.btnImport}
                    </LinkBlock>
                </LabelEditorImportStyled>
            }

        </LabelEditorSearchStyled>
    );
};

LabelEditorSearch.defaultProps = {
    isLabelsImportEnabled: false,
};

export default LabelEditorSearch;
